import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, AlertCircle, Calendar } from 'lucide-react';
import axios from 'axios';
import { Booking } from '../types';

// Use environment variable or fallback to localhost
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface RaiseIssueModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const RaiseIssueModal: React.FC<RaiseIssueModalProps> = ({ isOpen, onClose }) => {
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [isUrgent, setIsUrgent] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState('');
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Fetch user's bookings when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchBookings();
    }
  }, [isOpen]);

  const fetchBookings = async () => {
    try {
      setLoadingBookings(true);
      const token = localStorage.getItem('token');
      
      console.log('Fetching bookings for complaint modal...');
      const response = await axios.get(`${API_BASE_URL}/api/bookings/my-bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Bookings API response:', response.data);
      
      // Handle different API response structures
      let bookingsData = [];
      if (response.data?.data?.bookings) {
        bookingsData = response.data.data.bookings;
      } else if (response.data?.bookings) {
        bookingsData = response.data.bookings;
      } else if (Array.isArray(response.data?.data)) {
        bookingsData = response.data.data;
      } else if (Array.isArray(response.data)) {
        bookingsData = response.data;
      }
      
      console.log('Parsed bookings data:', bookingsData);
      
      // Sort by most recent first
      const sortedBookings = bookingsData.sort((a: Booking, b: Booking) => {
        return new Date(b.bookingDate).getTime() - new Date(a.bookingDate).getTime();
      });
      
      setBookings(sortedBookings);
      console.log('Set bookings count:', sortedBookings.length);
    } catch (err: any) {
      console.error('Error fetching bookings:', err);
      console.error('Error response:', err.response?.data);
      setBookings([]); // Set empty array on error
    } finally {
      setLoadingBookings(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!subject.trim() || !description.trim()) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/api/complaints`,
        {
          subject,
          description,
          isUrgent,
          bookingId: selectedBookingId || undefined
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setSuccess(true);
      
      // Auto close after showing success message
      setTimeout(() => {
        handleClose();
      }, 3000);
    } catch (err: any) {
      console.error('Error submitting complaint:', err);
      setError(err.response?.data?.message || 'Failed to submit complaint');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSubject('');
    setDescription('');
    setIsUrgent(false);
    setSelectedBookingId('');
    setError('');
    setSuccess(false);
    onClose();
  };

  const formatBookingDisplay = (booking: Booking) => {
    const bookingId = booking.bookingId || booking._id?.slice(-8) || booking.id?.slice(-8) || 'N/A';
    const serviceType = booking.serviceType === 'driver' ? '🚗 Driver' : '🔧 Service';
    const date = new Date(booking.bookingDate).toLocaleDateString();
    const statusEmoji = {
      'pending': '⏳',
      'confirmed': '✅',
      'completed': '🎉',
      'cancelled': '❌'
    }[booking.status] || '';
    
    return `${serviceType} - ID: ${bookingId} - ${date} ${statusEmoji}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" 
        onClick={handleClose}
      ></div>
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Raise an Issue</h2>
              <p className="text-sm text-gray-600 mt-1">
                We're here to help. Describe your issue and we'll get back to you.
              </p>
            </div>
            <button
              onClick={handleClose}
              disabled={submitting}
              className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {success ? (
            <div className="py-12 text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Complaint Submitted Successfully!</h3>
              <p className="text-gray-600 mb-4">
                Thank you for reaching out to us. Our customer care executive will contact you soon via call.
              </p>
              <p className="text-sm text-black600 font-medium">
                {isUrgent && '🚨 Your urgent request has been prioritized.'}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Urgent Toggle */}
              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isUrgent}
                    onChange={(e) => setIsUrgent(e.target.checked)}
                    disabled={submitting}
                    className="mt-1 h-5 w-5 text-red-600 rounded focus:ring-red-500"
                  />
                  <div className="ml-3">
                    <div className="flex items-center">
                      <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
                      <span className="font-semibold text-red-900">Mark as Urgent</span>
                    </div>
                    <p className="text-sm text-red-700 mt-1">
                      Check this box if your issue requires immediate attention (e.g., safety concerns, stuck in location, payment issues)
                    </p>
                  </div>
                </label>
              </div>

              {/* Related Booking Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-2" />
                    Related Booking (Optional)
                  </div>
                </label>
                {loadingBookings ? (
                  <div className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 text-sm flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></div>
                    Loading your bookings...
                  </div>
                ) : (
                  <select
                    value={selectedBookingId}
                    onChange={(e) => setSelectedBookingId(e.target.value)}
                    disabled={submitting}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:bg-gray-100"
                  >
                    <option value="">Select a booking (optional)</option>
                    {bookings.map((booking) => (
                      <option key={booking._id || booking.id} value={booking._id || booking.id}>
                        {formatBookingDisplay(booking)}
                      </option>
                    ))}
                  </select>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  {bookings.length > 0 
                    ? `Found ${bookings.length} booking${bookings.length !== 1 ? 's' : ''} - Select one if your issue is related to a specific booking`
                    : 'No bookings found - You can still submit a general complaint'}
                </p>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  disabled={submitting}
                  placeholder="Brief summary of your issue"
                  maxLength={200}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:bg-gray-100"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  {subject.length}/200 characters
                </p>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={submitting}
                  placeholder="Please provide detailed information about your issue..."
                  maxLength={2000}
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:bg-gray-100"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  {description.length}/2000 characters
                </p>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start">
                  <AlertCircle className="h-5 w-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-700">
                    <p className="font-medium mb-1">What happens next?</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Your complaint will be reviewed by our team</li>
                      <li>A customer care executive will reach out to you via call</li>
                      <li>Urgent requests are prioritized and handled immediately</li>
                      <li>You can track your complaint status in your profile</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={submitting}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || !subject.trim() || !description.trim()}
                  className={`flex-1 px-6 py-3 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed ${
                    isUrgent
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : 'bg-black hover:bg-black700 text-white'
                  }`}
                >
                  {submitting ? 'Submitting...' : isUrgent ? 'Submit Urgent Request' : 'Submit Complaint'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default RaiseIssueModal;

